/**
 * Provider Interface — the abstraction every LLM plugs into.
 *
 * A provider turns a prompt (+ conversation context) into a function-call
 * message. The orchestrator never knows which vendor is behind it. When
 * OpenAI is integrated later, implementing THIS interface is the only code
 * that needs writing — see adapters/openaiAdapter.js and README.md.
 *
 * No network code lives here or in any subclass shipped today.
 */

export class NotImplementedError extends Error {
  constructor(message) {
    super(message);
    this.name = 'NotImplementedError';
  }
}

/**
 * Abstract base. Subclasses implement `generate` (and optionally `stream`).
 *
 * generate(prompt, context) → Promise<{
 *   type: 'function_call' | 'text',
 *   name?: string,           // function name when type === 'function_call'
 *   arguments?: object,      // parsed function arguments
 *   text?: string,           // when type === 'text'
 *   usage?: object,          // provider-reported token/cost info
 * }>
 */
export class AIProvider {
  /** id/name/capabilities describe the provider to the UI and registry. */
  constructor({ id, name, capabilities = {} } = {}) {
    if (new.target === AIProvider) {
      throw new NotImplementedError('AIProvider is abstract — extend it');
    }
    this.id = id;
    this.name = name;
    this.capabilities = {
      functionCalling: false,
      streaming: false,
      offline: false,
      ...capabilities,
    };
  }

  /** True when the provider can be used right now (mock: yes; stubs: no). */
  isReady() {
    return false;
  }

  // eslint-disable-next-line no-unused-vars
  async generate(prompt, context) {
    throw new NotImplementedError(`${this.name}.generate() is not implemented`);
  }

  /**
   * Streaming variant — resolves to a StreamHandle (see streamingInterface).
   * Providers without streaming keep the default, which rejects.
   */
  // eslint-disable-next-line no-unused-vars
  async stream(prompt, context, onChunk) {
    throw new NotImplementedError(`${this.name} does not support streaming`);
  }
}

// ── Registry ─────────────────────────────────────────────────────────────────
// The playground lists every registered provider; only ready ones are usable.

const providers = new Map();

export const registerProvider = (provider) => {
  if (!(provider instanceof AIProvider)) {
    throw new Error('registerProvider: expected an AIProvider instance');
  }
  providers.set(provider.id, provider);
  return provider.id;
};

export const getProvider = (id) => {
  const provider = providers.get(id);
  if (!provider) throw new Error(`Unknown provider "${id}"`);
  return provider;
};

export const listProviders = () =>
  [...providers.values()].map((p) => ({
    id: p.id,
    name: p.name,
    ready: p.isReady(),
    capabilities: { ...p.capabilities },
  }));
